import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import { useSignIn } from "@/hooks/useSignIn";

interface BacklinkExchangeProps {
  onSignIn: () => void;
}

const BacklinkExchange: React.FC<BacklinkExchangeProps> = ({ onSignIn }) => {
  const { signingIn } = useSignIn();
  
  const handleClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };
  return (
    <section className="w-full py-12 sm:py-16 lg:py-20 bg-muted/20 relative overflow-hidden">
      {/* Background blur elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl opacity-30" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl opacity-30" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <ScrollReveal direction="up" delay={0}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-12 sm:mb-16 lg:mb-20">
              {/* Left Column - Heading and CTA */}
              <div className="flex flex-col justify-center text-center sm:text-left">
                <div className="mb-3 sm:mb-4">
                  <h1 className="text-xs sm:text-sm md:text-base font-bold text-foreground/90 uppercase tracking-wider">
                    BACKLINKS
                  </h1>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6 leading-tight">
                  Boost your <span className="text-primary">domain rating</span> with Monthly Backlinks
                </h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 leading-relaxed text-center sm:text-left">
                Get high-quality backlinks every month to increase your Domain Rating and drive organic traffic.
              </p>
              <Button
                onClick={handleClick}
                disabled={signingIn}
                className="w-full sm:w-auto rounded-full text-lg px-8 py-6 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg min-h-[60px] relative overflow-hidden group mx-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
                className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg min-h-[48px] sm:min-h-[60px] relative overflow-hidden group w-full sm:w-fit"
              >
                <span className="relative z-10 font-semibold tracking-wide">Grow Your Domain Rating</span>
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
              </Button>
              </div>

              {/* Right Column - Image/Illustration */}
              <div className="flex items-center justify-center">
                <img 
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/monthlybacklinks10.jpg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL21vbnRobHliYWNrbGlua3MxMC5qcGciLCJpYXQiOjE3NjQwODczNTcsImV4cCI6MTkyMTc2NzM1N30.XbTpEwBN2qhpzOg41WVfgTlNeUoExvJ9AWtIQqtq1Vw"
                  alt="Domain Rating Growth Chart"
                  className="w-full max-w-lg aspect-square rounded-3xl shadow-lg border border-border/50 object-cover"
                />
              </div>
            </div>
          </ScrollReveal>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Card 1 */}
            <ScrollReveal direction="up" delay={50}>
              <div className="bg-background rounded-2xl p-6 sm:p-8 border border-border/50 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 sm:mb-4">
                10 Premium Backlinks Monthly
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow">
                Get 10 high-authority backlinks (DA 70+) delivered to your dashboard every month. We handle everything—from finding the right sites to securing placements. No outreach, no hassle.
              </p>
              </div>
            </ScrollReveal>

            {/* Card 2 */}
            <ScrollReveal direction="up" delay={100}>
              <div className="bg-background rounded-2xl p-6 sm:p-8 border border-border/50 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 sm:mb-4">
                Full Transparency & Control
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow">
                See exactly where each backlink is placed. Track source domains, anchor text, placement status, and watch your domain authority climb in real-time—all from one dashboard.
              </p>
              </div>
            </ScrollReveal>

            {/* Card 3 */}
            <ScrollReveal direction="up" delay={150}>
              <div className="bg-background rounded-2xl p-6 sm:p-8 border border-border/50 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 sm:mb-4">
                Build Authority That Lasts
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow">
                Consistent, high-quality backlinks from authoritative sites boost your domain rating and search rankings. Watch your organic traffic grow month after month as your authority builds.
              </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BacklinkExchange;
